import {
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { Store } from '@ngrx/store';
import { RoleItem, Roles } from '@eview/core/models/roles';
import { selectRoles } from '@eview/core/store/selectors/roles.selector';
import { AppState } from '@eview/core/store/states/app.state';
import {
  UntypedFormGroup,
  UntypedFormBuilder,
  Validators
} from '@angular/forms';
import { AuthHelpers } from '@eview/core/auth';
import { AllowedPriviledge } from '@eview/core/auth/auth';
import { Observable, Subscription } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { UserService } from '@eview/core/users/user.service';
import { AuthCatalogFacade } from '@eview/core/store/facades/auth-catalog.facade';
import { selectRolesLoaded } from '@eview/core/store/selectors/auth-catalog.selectors';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'eview-roles-management',
  templateUrl: 'roles.component.html',
  styleUrls: ['roles.component.scss']
})
export class RolesComponent implements OnInit, OnDestroy {
  @ViewChild('roleModal') roleModal: TemplateRef<unknown>;

  roles$: Observable<Roles>;
  listLoading = false;

  constructor(
    private store: Store<AppState>,
    private userService: UserService,
    private formBuilder: UntypedFormBuilder,
    private modalService: NgbModal,
    private authCatalog: AuthCatalogFacade
  ) {
    this.subs = new Subscription();
    this.roles$ = this.store.select(selectRoles);
  }

  ngOnInit() {
    this.createRoleForm();
    this.store
      .select(selectRolesLoaded)
      .pipe(take(1))
      .subscribe(loaded => {
        if (!loaded) {
          this.listLoading = true;
        }
      });
    this.authCatalog.ensureRoles();
    this.subs.add(
      this.roles$.subscribe(roles => {
        if (roles != null) {
          this.rolesList = roles.results ?? [];
          this.listLoading = false;
        }
      })
    );
  }

  private refreshRolesList(): void {
    this.listLoading = true;
    this.authCatalog.ensureRoles(true);
  }

  createRoleForm() {
    this.roleForm = this.formBuilder.group({
      name			  : ['', [Validators.required, this.noSpecialCharsAtStart, Validators.maxLength(50)]],
      short_name	: ['', [Validators.nullValidator, this.noSpecialCharsAtStart, Validators.maxLength(255)]],
      display_name: ['', [Validators.required, this.noSpecialCharsAtStart, Validators.maxLength(50)]],
      description	: ['', [Validators.required, this.noSpecialCharsAtStart, Validators.maxLength(255)]],
    });
  }

  noSpecialCharsAtStart(control: any) {
    const value = control.value;
    if (!value) return null;
    const invalidChars = /^[.\-'"°,*()]$/;
    return invalidChars.test(value) ? { invalidStart: true } : null;
  }

  validarField(event: KeyboardEvent) {
    const char = event.key;
    const target = event.target as HTMLInputElement;
    const currentValue = target.value;
    const fieldName = target.getAttribute('formControlName');
    let maxLength;
    
    switch(fieldName) {
      case 'name':
      case 'display_name':
        maxLength = 50;
        break;
      case 'short_name':
      case 'description':
        maxLength = 255;
        break;
      default:
        maxLength = 255;
    }
    
    if (currentValue.length >= maxLength && !['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight'].includes(char)) {
      event.preventDefault();
      return;
    }
    
    if (char === '´' && currentValue.trim() === '') {
      event.preventDefault();
      return;
    }
    
    const selectionStart = target.selectionStart || 0;
    if (selectionStart === 0 && /[\s.\-'"°,*()]/.test(char)) {
      event.preventDefault();
      return;
    }
    
    const regex = /^[a-zA-Z0-9áéíóúÁÉÍÓÚüÜñÑ\s.\-'"°,*()]$/;
    if (!regex.test(char)) {
      event.preventDefault();
    }
  }

  onPaste(event: ClipboardEvent, fieldName: string) {
    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text') || '';
    let maxLength;
    
    switch(fieldName) {
      case 'name':
      case 'display_name':
        maxLength = 50;
        break;
      case 'short_name':
      case 'description':
        maxLength = 255;
        break;
      default:
        maxLength = 255;
    }
    
    let cleanedText = pastedText.replace(/^[\s.\-'"°,*()]+/, '').replace(/[^a-zA-Z0-9áéíóúÁÉÍÓÚüÜñÑ\s.\-'"°,*()]/g, '').substring(0, maxLength);
    this.roleForm.get(fieldName)?.setValue(cleanedText);
  }

  onCancelClick() {
    this.isEditing = false;
    this.editId = -1;
    this.roleForm.reset();
    this.modalService.dismissAll();
  }

  onEditClick(id: number) {
    this.isEditing = true;
    this.roles$.pipe(
      map(data => data?.results ?? []),
      take(1)
    ).subscribe((item) => {
      const role = item[id];
      if (role) {
        this.editId = role.id;
        this.roleForm.patchValue({
          name: role.name,
          short_name: role.short_name,
          display_name: role.display_name,
          description: role.description
        });
      }
    });
  }

  openRoleModal(index: number = -1) {
    if (!this.roleModal) {
      return;
    }
    if (index !== -1) {
      this.roles$.pipe(map(data => data?.results ?? []), take(1)).subscribe((items) => {
        const role = items[index];
        if (!role) {
          return;
        }
        this.isEditing = true;
        this.editId = role.id;
        this.roleForm.reset({
          name: role.name,
          short_name: role.short_name,
          display_name: role.display_name,
          description: role.description
        });
        this.modalService.open(this.roleModal, { centered: true, backdrop: 'static', size: 'lg' });
      });
      return;
    }
    this.isEditing = false;
    this.editId = -1;
    this.roleForm.reset();
    this.modalService.open(this.roleModal, { centered: true, backdrop: 'static', size: 'lg' });
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  onDeleteClick(index: number) {
    this.selectedId = index;
  }

  onConfirmClick(roleId: number) {
    if (roleId) {
      this.userService.deleteRole(roleId).subscribe(() => {
        this.selectedId = -1;
        this.refreshRolesList();
      });
    }
  }

  onSubmitClick() {
    if (!this.roleForm.valid) {
      return;
    }
    const reqBody = this.roleForm.value;
    if (!this.isEditing) {
      this.userService.addRoles(reqBody).subscribe(() => {
        this.roleForm.reset();
        this.refreshRolesList();
        this.modalService.dismissAll();
      });
    } else {
      this.userService.updateRoles(reqBody, this.editId).subscribe(() => {
        this.isEditing = false;
        this.roleForm.reset();
        this.refreshRolesList();
        this.modalService.dismissAll();
      });
    }
  }

  subs: Subscription;

  AuthHelpers = AuthHelpers;

  roleForm: UntypedFormGroup;

  AllowedPriviledge = AllowedPriviledge;

  selectedId: number = -1;

  editId: number = -1;

  isEditing: boolean = false;

  rolesList: RoleItem[] = [];
}
